import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import ProjectTabs, { useWorkSummary } from "../components/work/ProjectTabs";
import WorkCard from "../components/work/WorkCard";
import QuickFilters, { EMPTY_FILTERS, hasFilters, isQueryMode, useProjectFilters } from "../components/work/QuickFilters";
import QueryBar from "../components/work/QueryBar";
import { Avatar } from "../components/work/workMeta";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

const ALL = "__all__";

export function useWorkItems(projectId, filters, params, transform) {
  const [items, setItems] = useState(null);
  const [queryError, setQueryError] = useState(null);
  const queryMode = isQueryMode(filters);
  const query = filters.query;
  const load = useCallback(() => {
    if (params === null) return;
    const req = queryMode && query
      ? api.get(`/projects/${projectId}/work-items/query`, { params: { q: query } })
      : queryMode ? Promise.resolve({ data: [] }) : api.get(`/projects/${projectId}/work-items`, { params });
    req.then((r) => { setItems(transform ? transform(r.data) : r.data); setQueryError(null); })
      .catch((e) => { const msg = apiError(e); if (queryMode) { setQueryError(msg); setItems([]); } else toast.error(msg); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, queryMode, query, JSON.stringify(params)]);
  useEffect(() => { load(); }, [load]);
  return { items, setItems, load, queryError };
}

export function BoardFilters({ summary, filters, setFilters, extra, queryError, count }) {
  const { t } = useLang();
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v === ALL ? "" : v }));
  if (isQueryMode(filters)) {
    return (
      <div className="space-y-2" data-testid="board-filters">
        <QueryBar value={filters.query} error={queryError} count={count} onRun={(q) => setFilters((f) => ({ ...f, query: q }))} />
        {extra && <div className="flex flex-wrap items-center gap-2">{extra}</div>}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="board-filters">
      <Input data-testid="board-search" className="h-8 w-48 text-sm" placeholder={t("searchWork")} value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
      <Select value={filters.assignee || ALL} onValueChange={set("assignee")}>
        <SelectTrigger className="h-8 w-40 text-xs" data-testid="filter-assignee"><SelectValue placeholder={t("assignee")} /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>{t("assignee")}: {t("all")}</SelectItem><SelectItem value="me">{t("me")}</SelectItem><SelectItem value="unassigned">{t("unassigned")}</SelectItem>
          {summary.members.map((m) => <SelectItem key={m.id} value={m.id}>{m.display_name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={filters.type || ALL} onValueChange={set("type")}>
        <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-type"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>{t("type")}: {t("all")}</SelectItem>{summary.types.map((x) => <SelectItem key={x} value={x}>{t(x)}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={filters.priority || ALL} onValueChange={set("priority")}>
        <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-priority"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>{t("priority")}: {t("all")}</SelectItem>{summary.priorities.map((x) => <SelectItem key={x} value={x}>{t(x)}</SelectItem>)}</SelectContent>
      </Select>
      {summary.labels.length > 0 && (
        <Select value={filters.label || ALL} onValueChange={set("label")}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-label"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>{t("labels")}: {t("all")}</SelectItem>{summary.labels.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><input type="checkbox" data-testid="filter-blocked" checked={!!filters.blocked} onChange={(e) => setFilters((f) => ({ ...f, blocked: e.target.checked }))} />{t("blockedOnly")}</label>
      {extra}
      {hasFilters(filters) && (
        <button className="text-xs text-[#1E3A8A] hover:underline" data-testid="filters-clear" onClick={() => setFilters(EMPTY_FILTERS)}>{t("clearFilters")}</button>
      )}
    </div>
  );
}

export function filterCtx(summary, user) {
  return { meId: user?.id, blockedStatusIds: new Set(summary.workflow.statuses.filter((s) => s.is_side && s.category !== "DONE").map((s) => s.id)) };
}

export function applyFilters(items, f, ctx = {}) {
  const assignee = f.assignee === "me" ? ctx.meId : f.assignee;
  return items.filter((i) =>
    (!f.q || i.key.toLowerCase().includes(f.q.toLowerCase()) || i.title.toLowerCase().includes(f.q.toLowerCase())) &&
    (!f.assignee || (assignee === "unassigned" ? !i.assignee_id : i.assignee_id === assignee)) &&
    (!f.type || i.type === f.type) && (!f.priority || i.priority === f.priority) &&
    (!f.label || (i.labels || []).includes(f.label)) &&
    (!f.blocked || i.is_blocked || (ctx.blockedStatusIds && ctx.blockedStatusIds.has(i.status_id))));
}

function Column({ status, items, canDrag, laneKey, currency }) {
  const { t } = useLang();
  const pts = items.reduce((a, i) => a + (i.story_points || 0), 0);
  return (
    <div className="flex flex-col min-w-[260px] w-[260px] bg-secondary/50 rounded-lg border" data-testid={`board-column-${status.id}`}>
      <div className="px-3 py-2 flex items-center justify-between border-b">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">{status.name}</div>
        <div className="text-[11px] text-muted-foreground whitespace-nowrap" data-testid={`board-column-count-${status.id}`}>{items.length}{pts ? ` · ${pts} ${t("points")}` : ""}</div>
      </div>
      <Droppable droppableId={`${laneKey}|${status.id}`} isDropDisabled={!canDrag}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.droppableProps}
            className={`flex-1 p-2 space-y-2 min-h-[120px] transition-colors ${snapshot.isDraggingOver ? "bg-[#DBEAFE]/60" : ""}`}>
            {items.map((item, idx) => (
              <Draggable key={item.id} draggableId={item.id} index={idx} isDragDisabled={!canDrag}>
                {(p, snap) => (
                  <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} className={snap.isDragging ? "rotate-[1deg]" : ""}>
                    <WorkCard item={item} currency={currency} />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

export default function Board() {
  const { projectId } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const { summary, sprints, reload } = useWorkSummary(projectId);
  const [filters, setFilters] = useProjectFilters(projectId);
  const [swimlane, setSwimlane] = useState("none");
  const [showCanceled, setShowCanceled] = useState(false);
  const listParams = useMemo(() => {
    if (!summary) return null;
    return summary.project.methodology !== "KANBAN" && summary.active_sprint ? { sprint_id: summary.active_sprint.id } : {};
  }, [summary]);
  const { items, setItems, load, queryError } = useWorkItems(projectId, filters, listParams);

  const statuses = useMemo(() => {
    if (!summary) return [];
    return [...summary.workflow.statuses].sort((a, b) => a.order - b.order)
      .filter((s) => showCanceled || !(s.is_side && s.category === "DONE"))
      .filter((s) => summary.project.methodology === "KANBAN" || !s.is_initial || !summary.active_sprint);
  }, [summary, showCanceled]);

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    const toStatus = destination.droppableId.split("|")[1];
    const fromStatus = source.droppableId.split("|")[1];
    if (toStatus === fromStatus) return;
    const item = items.find((i) => i.id === draggableId);
    const target = summary.workflow.statuses.find((s) => s.id === toStatus);
    const prev = items;
    setItems(items.map((i) => (i.id === draggableId ? { ...i, status_id: target.id, status_name: target.name, status_category: target.category } : i)));
    try {
      await api.post(`/work-items/${item.key}/transitions`, { to_status_id: target.id });
      toast.success(`${item.key} → ${target.name}`);
      load();
    } catch (e) { toast.error(apiError(e)); setItems(prev); }
  };

  if (!summary || !items) return <Skeleton className="h-96 w-full" />;
  const visible = applyFilters(items, filters, filterCtx(summary, user));
  const canDrag = summary.access !== "READ";
  const isScrum = summary.project.methodology !== "KANBAN";
  const lanes = swimlane === "none" ? [{ key: "all", label: null, items: visible }]
    : swimlane === "assignee"
      ? [...summary.members.map((m) => ({ key: m.id, label: m.display_name, items: visible.filter((i) => i.assignee_id === m.id) })),
         { key: "unassigned", label: t("unassigned"), items: visible.filter((i) => !i.assignee_id) }].filter((l) => l.items.length)
      : [...summary.epics.map((e) => ({ key: e.id, label: `${e.key} · ${e.title}`, items: visible.filter((i) => i.parent_id === e.id) })),
         { key: "noepic", label: t("noEpic"), items: visible.filter((i) => !summary.epics.some((e) => e.id === i.parent_id)) }].filter((l) => l.items.length);

  return (
    <div className="space-y-4" data-testid="board-page">
      <ProjectTabs summary={summary} sprints={sprints} onCreated={() => { reload(); load(); }} />
      {isScrum && (
        <div className="apha-card px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm" data-testid="board-sprint-banner">
          {summary.active_sprint ? (
            <>
              <span className="font-heading font-semibold">{summary.active_sprint.name}</span>
              <span className="text-muted-foreground">{summary.active_sprint.start_date} → {summary.active_sprint.end_date}</span>
              {summary.active_sprint.goal && <span className="text-muted-foreground italic truncate">“{summary.active_sprint.goal}”</span>}
            </>
          ) : <span className="text-muted-foreground">{t("noActiveSprint")}</span>}
        </div>
      )}
      <QuickFilters projectId={projectId} summary={summary} filters={filters} setFilters={setFilters} swimlane={swimlane} setSwimlane={setSwimlane} />
      <BoardFilters summary={summary} filters={filters} setFilters={setFilters} queryError={queryError} count={items.length} extra={
        <>
          <Select value={swimlane} onValueChange={setSwimlane}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="swimlane-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">{t("swimlanes")}: {t("none")}</SelectItem><SelectItem value="assignee">{t("byAssignee")}</SelectItem><SelectItem value="epic">{t("byEpic")}</SelectItem></SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><input type="checkbox" data-testid="show-canceled" checked={showCanceled} onChange={(e) => setShowCanceled(e.target.checked)} />{t("showCanceled")}</label>
        </>
      } />
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="space-y-6">
          {lanes.map((lane) => (
            <div key={lane.key} data-testid={`swimlane-${lane.key}`}>
              {lane.label && <div className="flex items-center gap-2 text-sm font-medium mb-2">{swimlane === "assignee" && <Avatar name={lane.key === "unassigned" ? null : lane.label} />}{lane.label} <span className="text-xs text-muted-foreground">({lane.items.length})</span></div>}
              <div className="flex gap-3 overflow-x-auto pb-3">
                {statuses.map((s) => <Column key={`${lane.key}-${s.id}`} laneKey={lane.key} status={s} items={lane.items.filter((i) => i.status_id === s.id)} canDrag={canDrag} currency={summary.project.currency} />)}
              </div>
            </div>
          ))}
        </div>
      </DragDropContext>
      {items.length === 0 && <div className="text-sm text-muted-foreground" data-testid="board-empty">{t("noWorkItems")}</div>}
    </div>
  );
}
